<template>
  <div class="values-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-tag-multiple</v-icon>
                {{ isDeletePage() ? 'Deleted Values' : 'Values Management' }}
                <span class="page-inline-subtitle">{{ isDeletePage() ? 'Manage deleted values and restore them when needed' : 'Manage system values and their configurations' }}</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions" v-if="!isDeletePage()">
                <v-btn 
                  color="primary" 
                  to="/values/create"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Value
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Values Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="values-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      {{ isDeletePage() ? 'Deleted Values' : 'All Values' }}
                    </h2>
                    <div class="results-count" v-if="count > 0">
                      <div class="count-display">
                        <span class="count-number">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Clean Filters Section -->
                  <div class="filters-section">
                    <v-text-field
                      v-model="filter.search_keyword"
                      placeholder="Search values..."
                      prepend-inner-icon="mdi-magnify"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-search clean-filter"
                      @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                      @keydown.enter="(page = 1), getData()"
                    ></v-text-field>
                    
                    <v-select
                      v-model="filter.is_active"
                      :items="statuses"
                      placeholder="Status"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-check-circle-outline</v-icon>
                      </template>
                    </v-select>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="resetFilters()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>
                      
                      <v-btn
                        v-if="values.length > 0 && !isDeletePage()"
                        color="#10b981"
                        @click="generateExcel()"
                        :loading="isExcelloading"
                        class="filter-export-btn clean-btn"
                        small
                      >
                        <v-icon small left>mdi-download</v-icon>
                        Export
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="values"
                :loading="isLoading"
                :server-items-length="count"
                :items-per-page="rowsPerPage"
                :page="page"
                @update:page="updatePage"
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (page - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active == 1 ? 'green' : 'red'"
                  dark
                  small
                >
                  {{ item.is_active == 1 ? 'ACTIVE' : 'INACTIVE' }}
                </v-chip>
              </template>

              <template v-slot:[`item.name`]="{ item }">
                <div class="value-info">
                  <div class="value-name">{{ item.name || '' }}</div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  :to="`/values/${encryptIt(item.id)}`"
                  color="info"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  v-if="!isDeletePage()"
                  icon
                  small
                  :to="`/values/${encryptIt(item.id)}`"
                  color="primary"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="!isDeletePage()"
                  icon
                  small
                  @click="Delete_this(item.id)"
                  color="error"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-if="isDeletePage()"
                  icon
                  small
                  @click="Restore_this(item.id)"
                  color="success"
                >
                  <v-icon>mdi-delete-restore</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="page"
                  :length="paginationLength"
                  :total-visible="7"
                  color="primary"
                  @input="getData"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

        <!-- Success Dialogs -->
        <v-dialog v-model="is_deleted_value" width="600">
          <v-card rounded="xxl" class="glassmorphism-card">
            <v-container>
              <v-row align="center">
                <v-col sm="3" align="center">
                  <v-icon
                    large
                    style="color: green; font-size: 90px; width: 200px !important"
                  >
                    mdi-delete-empty
                  </v-icon>
                </v-col>
                <v-col sm="9" align="center">
                  <v-card-text>
                    <v-row>
                      <v-col>
                        <h1 style="color: green">Hurray!</h1>
                        <br />
                        <h3>Value deleted successfully.</h3>
                        <br />
                        <v-btn
                          color="success"
                          rounded
                          class="glassmorphism-btn"
                          @click="is_deleted_value = false"
                        >
                          OK
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-dialog>

        <v-dialog v-model="is_restored_value" width="600">
          <v-card rounded="xxl" class="glassmorphism-card">
            <v-container>
              <v-row align="center">
                <v-col sm="3" align="center">
                  <v-icon
                    large
                    style="color: green; font-size: 90px; width: 200px !important"
                  >
                    mdi-delete-restore
                  </v-icon>
                </v-col>
                <v-col sm="9" align="center">
                  <v-card-text>
                    <v-row>
                      <v-col>
                        <h1 style="color: green">Hurray!</h1>
                        <br />
                        <h3>Value restored successfully.</h3>
                        <br />
                        <v-btn
                          color="success"
                          rounded
                          class="glassmorphism-btn"
                          @click="is_restored_value = false"
                        >
                          OK
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";
import mixin from "@/mixin";
export default {
  mixins: [mixin],
  name: "valuesPage",
  data() {
    return {
      filter: {
        search_keyword: null,
        is_active: null,
      },
      values: [],
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Status', value: 'is_active', sortable: true, width: '120px' },
        { text: 'Value Name', value: 'name', sortable: false, width: '300px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      statuses: [
        { text: 'All', value: null },
        { text: 'Active', value: 1 },
        { text: 'Inactive', value: 0 }
      ],
      page: 1,
      rowsPerPage: 15,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      isExcelloading: false,
      delete_value: false,
      is_deleted_value: false,
      restore_value: false,
      is_restored_value: false,
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      console.log("Get Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active !== null ? this.filter.is_active : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;
      await axios
        .get(`values?${query}`)
        .then((response) => {
          this.values = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          // Only redirect to login if it's actually an authentication error
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },
    //Generate Excel
    async generateExcel() {
      this.isExcelloading = true;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active !== null ? this.filter.is_active : "";
      // eslint-disable-next-line no-unused-vars
      let query = 
        "is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;
      // TODO: Implement excel export endpoint
      // let response = await axios.get(`value_exports?${query}`);
      // console.log(response.data.data);
      // if (response) {
      //   window.open(`${response.data.data}`, "_blank");
      // }
      console.log("Excel export not implemented yet");
      this.isExcelloading = false;
    },
    isDeletePage() {
      let response = false;
      if (this.$route.query.open_trash) {
        response = true;
      }
      return response;
    },
    async Delete_this(id) {
      this.delete_value = true;
      await axios
        .post(`values/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_value = false;
          this.is_deleted_value = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    async Restore_this(id) {
      this.restore_value = true;
      await axios
        .post(`values/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_value = false;
          this.is_restored_value = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    updatePage(newPage) {
      this.page = newPage;
      this.getData();
    },
    resetFilters() {
      this.filter.search_keyword = null;
      this.filter.is_active = null;
      this.page = 1;
      this.getData();
    },
  },
};
</script>

<style scoped>
.values-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Values Table Card */
.values-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Prominent Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

/* Clean Filter Styling */
.clean-filter {
  margin: 0 !important;
}

.clean-filter >>> .v-input__control {
  min-height: 40px !important;
}

.clean-filter >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  padding: 0 16px !important;
  margin: 0 !important;
}

.clean-filter >>> .v-input__control .v-input__slot:before,
.clean-filter >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.clean-filter >>> .v-input__control .v-input__slot:hover {
  border-color: #9ca3af !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-filter >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #64748b !important;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.15) !important;
}

/* Remove any fieldset or additional borders */
.clean-filter >>> fieldset {
  display: none !important;
}

.clean-filter >>> .v-input__control:before,
.clean-filter >>> .v-input__control:after {
  display: none !important;
}

/* Search Filter - Wider */
.filter-search.clean-filter {
  width: 280px !important;
  min-width: 280px !important;
}

/* Select Filters */
.filter-select.clean-filter {
  width: 160px !important;
  min-width: 160px !important;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Export Button */
.filter-export-btn.clean-btn {
  background: #10b981 !important;
  color: white !important;
  border: none !important;
}

.filter-export-btn.clean-btn:hover {
  background: #059669 !important;
}

/* Clean Filter Text Styling */
.clean-filter >>> .v-text-field__details {
  display: none !important;
}

.clean-filter >>> input,
.clean-filter >>> .v-select__selection {
  font-size: 14px !important;
  color: #1e293b !important;
  font-weight: 500;
}

.clean-filter >>> input::placeholder {
  color: #94a3b8 !important;
  font-size: 14px !important;
  font-weight: 400;
}

.clean-filter >>> .v-select__selection--comma {
  color: #1e293b !important;
}

/* Icons in clean filters */
.clean-filter >>> .v-icon {
  color: #64748b !important;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* Value Info Styling */
.value-info {
  display: flex;
  flex-direction: column;
}

.value-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
  font-size: 14px;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Action Buttons in Table */
.v-btn--icon {
  border-radius: 8px !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filters-single-row {
    gap: 16px;
  }
  
  .filter-search.clean-filter {
    width: 240px !important;
    min-width: 240px !important;
  }
  
  .filter-select.clean-filter {
    width: 140px !important;
    min-width: 140px !important;
  }
}

@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 200px !important;
    min-width: 200px !important;
  }
  
  .filter-actions {
    justify-content: center;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 100% !important;
    min-width: auto !important;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
}
</style>